import React, { useContext, useRef } from "react";
import {
  Button,
  Popover,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";
import styled from "styled-components";
import { observer } from "mobx-react";
import ApiClient from "utils/ApiClient";
import { StyledTooltip } from "../common/utils/StyledTooltip";
import TextField from "@material-ui/core/TextField";
import { PackStoreContext } from "app/Pack/PackStore.js";
import { handleSocketConnection } from "app/websocketManager/eventEmitter";

function ImportSheetPopup({ anchorEl, isOpen, onClose }) {
  const [isSheetCreating, setIsSheetCreating] = React.useState(false);
  const [fileName, setFileName] = React.useState("Choose .csv file");
  const [buttonText, setButtonText] = React.useState("Create");
  const [isTextOverFlown, setTextOverFlown] = React.useState(false);
  const store = useContext(PackStoreContext);
  const textFieldRef = useRef("");
  const fileRef = useRef(null);

  React.useEffect(() => {
    setTimeout(() => {
      let isOverFlown =
        fileRef?.current?.scrollWidth > fileRef?.current?.clientWidth;
      if (isOverFlown) {
        setTextOverFlown(true);
      } else {
        setTextOverFlown(false);
      }
    }, 1);
  }, [fileName]);

  const handleFileClick = () => {
    document.getElementsByClassName("importSheetFile")[0].click();
  };

  const getErrorMessage = (value, message) => {
    return {
      isValidationSuccess: value,
      errorMessage: message,
    };
  };

  const validateForm = () => {
    let files = document.getElementsByClassName("importSheetFile")[0].files;
    let sheetName = textFieldRef.current.value;
    if (sheetName == "") {
      if (files.length == 0) {
        return getErrorMessage(
          false,
          "Please enter the sheet name and choose the .csv file"
        );
      }
      return getErrorMessage(false, "Please enter the sheet name");
    } else {
      if (files.length == 0) {
        return getErrorMessage(false, "Please choose .csv file");
      }
      return { isValidationSuccess: true };
    }
  };

  const handleClose = () => {
    setIsSheetCreating(false);
    setButtonText("Create");
    setFileName("Choose .csv file");
    setTextOverFlown(false);
    onClose();
  };

  const showNewlyCreatedSheet = async (listId) => {
    handleClose();
    try {
      const response = await ApiClient.List.getListConfig({
        list_id: listId,
      });
      store.appStore.leftHoverMenuStore.addPackList(response.data.list);
      const sheetData = response.data.list;
      if(response.success && sheetData.is_log_sheet == '0' && sheetData.parent_list_id == '0' && store.appStore?.allUserLists && Array.isArray(store.appStore.allUserLists) && store.appStore.allUserLists.length > 0) {
        store.appStore.allUserLists = [...store.appStore.allUserLists, sheetData];
      }
      store.appStore.routerHistory.push(`/pack/${store.id}/list/${listId}`);
      window.appStore.notificationStore.showNotification(
        (response.data.list?.options?.first_time_message) ?? "Records imported successfully",
        "success"
      );
    } catch (e) {
      window.appStore.notificationStore.showNotification(
        "something went wrong.please refresh to see the newly created sheet.",
        "error"
      );
    }
  };

  const handleCreateSheet = async () => {
    let response = validateForm();
    let files = document.getElementsByClassName("importSheetFile")[0].files;
    let sheetName = textFieldRef.current.value;
    if (response.isValidationSuccess == true) {
      setButtonText("Uploading...");
      setIsSheetCreating(true);
      try {
        const resp = await ApiClient.Packs.importExcelSheetWithDefaultOptions({
          text: sheetName,
          rb: 1,
          parent_id: 0,
          pack_id: store.id,
          sheet: files[0],
        });
        let importId = resp.data;
        let listId = resp.listId;
        pollForCompletion(importId, listId);
      } catch (e) {
        setButtonText("Create");
        setIsSheetCreating(false);
        window.appStore.notificationStore.showNotification(e.message, "error");
      }
    } else {
      window.appStore.notificationStore.showNotification(
        response.errorMessage,
        "caution"
      );
    }
  };

  const pollForCompletion = async (importId, listId) => {
    try {
      const response = await ApiClient.Packs.importStatus({
        import_id: importId,
      });
      if(response.success) {
        if (parseInt(response.data.is_completed)) {
          handleSocketConnection(listId);
          showNewlyCreatedSheet(listId);
        } else {
          setButtonText(response.data.completion + " records created");
          pollForCompletion(importId, listId);
        }
      } else {
        window.appStore.notificationStore.showNotification("Importing Failed", "error");
      }
    } catch (e) {
      window.appStore.notificationStore.showNotification(e.message, "error");
    }
  };

  const setChoosenFileName = (e) => {
    let files = e.target?.files;
    if (files.length > 0) {
      let fileName = e.target.files[0].name;
      setFileName(fileName);
    }
  };

  return (
    <PopoverStyled
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={isOpen}
      onClose={handleClose}
      anchorEl={anchorEl}
      maxWidth="xl"
    >
      <DialogContent>
        <form
          id="myForm"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <StyledDiv>
            <TextField
              className="importSheetName"
              label="Enter Sheet Name"
              variant="outlined"
              type="text"
              name="text"
              inputRef={textFieldRef}
            />
          </StyledDiv>
          <StyledTooltip
            title={fileName}
            disableHoverListener={!isTextOverFlown}
          >
            <StyledButton
              onClick={handleFileClick}
              variant="contained"
              color="primary"
            >
              <StyledFileName ref={fileRef}>{fileName}</StyledFileName>
            </StyledButton>
          </StyledTooltip>
          <input
            type="file"
            className="importSheetFile"
            name="sheet"
            hidden
            accept=".csv"
            onChange={setChoosenFileName}
          ></input>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateSheet}
          disabled={isSheetCreating}
        >
          {isSheetCreating ? <StyledSpan>{buttonText}</StyledSpan> : buttonText}
          {isSheetCreating && <CircularProgress size={18} color="inherit" />}
        </Button>
      </DialogActions>
    </PopoverStyled>
  );
}

const PopoverStyled = styled(({ className, ...props }) => (
  <Popover {...props} classes={{ paper: className }} />
))`
  width: 400px;
`;

const StyledDiv = styled.div`
  margin-bottom: 15px;
`;

const StyledSpan = styled.span`
  color: green;
`;

const StyledButton = styled(Button)`
  max-width: 200px;
`;
const StyledFileName = styled.span`
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
export default observer(ImportSheetPopup);
